import { createSlice, createAsyncThunk, PayloadAction, createSelector } from '@reduxjs/toolkit';
import { directusOperations } from '../../lib/directus';
import { getWIBTimeForDirectus } from '../../lib/timeUtils';

/**
 * Database-aware normalization for 'done' field
 * Handles various formats that might come from different database backends
 */
function normalizeDoneValue(done: any): boolean | null | undefined {
  // Handle explicit true values (completed)
  if (done === true || done === 1 || done === '1' || done === 'true' || done === 'TRUE') {
    return true;
  }
  
  // Handle explicit false values (cancelled)
  if (done === false || done === 0 || done === '0' || done === 'false' || done === 'FALSE') {
    return false;
  }
  
  // Handle blank values (pending/overdue - let date logic decide)
  if (done === null || done === undefined || done === '') {
    return null;
  }
  
  // Fallback: unexpected values default to null (safer than error)
  console.warn('⚠️ Unexpected done value, defaulting to null:', done, typeof done);
  return null;
}

/**
 * Validate and normalize interaction data from database
 * Matches actual customer_Interaction table schema
 */
function normalizeInteractionData(item: any): Partial<CustomerInteractionRecord> {
  return {
    id: item.id,
    No_pelanggan: item.No_pelanggan || 'UNKNOWN',
    date_created: item.date_created || new Date().toISOString(),
    priority: ['low', 'medium', 'high'].includes(item.priority) ? item.priority : 'low',
    keterangan: item.keterangan || '',
    interaction_type: item.interaction_type || 'WhatsApp',
    PIC: item.PIC || 'UNKNOWN',
    customer_tambak_id: item.customer_tambak_id || null,
    visit_id: item.visit_id || null,
    done: normalizeDoneValue(item.done), // Critical: normalize done field
    hidden: Boolean(item.Hidden), // Soft delete field
    // Display names from joins
    customer_name: item.customer_name || 'Unknown Customer',
    pic_name: item.pic_name || 'Unknown PIC',
    tambak_name: item.tambak_name || null
  };
}

// Interface for customer interaction record from database (matches actual schema)
export interface CustomerInteractionRecord {
  id: number;
  No_pelanggan: string;
  date_created: string;
  date_remind?: string; // Scheduled reminder date
  priority?: 'low' | 'medium' | 'high';
  keterangan?: string;
  interaction_type: string;
  PIC: string;
  customer_tambak_id?: number | null;
  visit_id?: number | null;
  done?: boolean; // Status field for interaction completion
  hidden?: boolean; // Soft delete field - true when record is deleted
  file?: (string | number)[]; // Array of file IDs from customerInteractionFile junction table
  // Related data from API joins
  customer_name?: string;
  pic_name?: string;
  tambak_name?: string;
  // Activity sorting metadata (added for dashboard activity interactions)
  relevantDate?: string; // ✅ Store as ISO string, not Date object
  isOverdue?: boolean;
  isToday?: boolean;
  timeUntilReminder?: number | null;
}

// State interface
export interface InteractionsState {
  // Data
  interactions: CustomerInteractionRecord[];
  todayInteractions: CustomerInteractionRecord[];
  selectedInteraction: CustomerInteractionRecord | null;
  
  // Loading states
  isLoading: boolean;
  isTodayLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  
  // Error states
  error: string | null;
  
  // Pagination and filters
  totalCount: number;
  currentPage: number;
  pageSize: number;
  searchTerm: string;
  filterType: string;
  
  // Cache management
  lastUpdated: string | null;
  todayLastFetched: string | null;
  isStale: boolean;
}

const initialState: InteractionsState = {
  interactions: [],
  todayInteractions: [],
  selectedInteraction: null,
  
  isLoading: false,
  isTodayLoading: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  
  error: null,
  
  totalCount: 0,
  currentPage: 1,
  pageSize: 1000, // Increased limit to show more historical data
  searchTerm: '',
  filterType: 'all',
  
  lastUpdated: null,
  todayLastFetched: null,
  isStale: true,
};

// Async thunks
export const fetchCustomerInteractions = createAsyncThunk(
  'interactions/fetchCustomerInteractions',
  async (params: { 
    limit?: number; 
    offset?: number; 
    search?: string;
    filterType?: string;
  } = {}, { rejectWithValue }) => {
    try {
      const { limit = 50, offset = 0, search, filterType = 'all' } = params;
      
      let additionalFilter: any = {
        _and: [
          // Always filter out hidden (soft deleted) interactions
          {
            _or: [
              { Hidden: { _null: true } },
              { Hidden: { _eq: false } }
            ]
          }
        ]
      };
      
      // Apply search filter
      if (search) {
        additionalFilter._and.push({
          _or: [
            { keterangan: { _icontains: search } },
            { 'No_pelanggan.Nama_Pelanggan': { _icontains: search } },
            { interaction_type: { _icontains: search } }
          ]
        });
      }
      
      // Apply type filter
      if (filterType !== 'all') {
        additionalFilter._and.push({
          interaction_type: { _eq: filterType }
        });
      }
      
      // 🛡️ SECURITY FIX: Use getUserCustomerInteractionsWithFilter to enforce PIC filtering
      const result = await directusOperations.getUserCustomerInteractionsWithFilter({
        limit,
        offset,
        additionalFilter,
        sort: ['-date_created']
      });
      
      // Transform using robust normalization
      const interactions = result.map((item: any) => normalizeInteractionData(item));
      
      return {
        interactions,
        totalCount: result.length, // Note: This is not the actual total, would need separate count query
        currentPage: Math.floor(offset / limit) + 1
      };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch customer interactions');
    }
  }
);

export const fetchUserInteractions = createAsyncThunk(
  'interactions/fetchUserInteractions',
  async (limit: number = 1000, { rejectWithValue }) => {
    try {
      // Use getUserCustomerInteractionsWithFilter to filter out hidden interactions
      const result = await directusOperations.getUserCustomerInteractionsWithFilter({
        limit: limit, // Pass the actual limit parameter
        additionalFilter: {
          _or: [
            { Hidden: { _null: true } },
            { Hidden: { _eq: false } }
          ] // Filter out soft deleted interactions (show null or false)
        },
        sort: ['-date_created']
      });

      // Data should now be clean from the API layer
      return result.map((item: any) => normalizeInteractionData(item));
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch user interactions');
    }
  }
);

export const createCustomerInteraction = createAsyncThunk(
  'interactions/createCustomerInteraction',
  async (interactionData: {
    No_pelanggan: string;
    keterangan?: string;
    interaction_type?: string;
    customer_tambak_id?: number;
    visit_id?: number;
    date_remind?: string;
    priority?: 'low' | 'medium' | 'high';
    date_created?: string;
    done?: boolean;
    isScheduledNow?: boolean; // Flag to indicate if this is an immediate "now" scheduling
  }, { rejectWithValue, dispatch }) => {
    try {
      // Determine done status based on business logic
      let doneStatus = interactionData.done; // Use provided value if explicitly set
      
      if (doneStatus === undefined) {
        // FIXED: Business logic for done status - create as pending by default
        // 1. If has date_remind (scheduled for future), set done=null (pending)
        // 2. Otherwise, also set done=null (pending) - user needs to mark complete manually
        // 3. Only explicitly completed interactions should have done=true
        if (interactionData.date_remind) {
          // Has scheduled date, set done=null as pending until completed
          doneStatus = null;
        } else {
          // Default for new interactions - create as pending, not auto-completed
          doneStatus = null;
        }
      }
      
      // Create the interaction with determined done status
      const dataToCreate = {
        ...interactionData,
        done: doneStatus
      };
      
      // Remove the isScheduledNow flag as it's not part of the database schema
      const cleanData = { ...dataToCreate };
      delete cleanData.isScheduledNow;
      
      const result = await directusOperations.createCustomerInteraction(cleanData);
      
      // Refresh the interactions list
      dispatch(fetchCustomerInteractions());
      
      return result;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to create customer interaction');
    }
  }
);

export const updateCustomerInteraction = createAsyncThunk(
  'interactions/updateCustomerInteraction',
  async (params: {
    id: number;
    data: {
      keterangan?: string;
      interaction_type?: string;
      customer_tambak_id?: number | null;
      visit_id?: number | null;
      date_remind?: string;
      priority?: 'low' | 'medium' | 'high';
      done?: boolean | null;
      date_created?: string; // Allow rescheduling
      file?: (string | number)[]; // Array of file IDs from customerInteractionFile junction table
    }
  }, { rejectWithValue, dispatch }) => {
    try {
      const result = await directusOperations.updateCustomerInteraction(params.id, params.data);
      
      // Refresh the interactions list
      dispatch(fetchCustomerInteractions());
      
      return result;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to update customer interaction');
    }
  }
);

export const markInteractionAsDone = createAsyncThunk(
  'interactions/markInteractionAsDone',
  async (interactionId: number, { rejectWithValue, dispatch }) => {
    try {
      await directusOperations.updateCustomerInteraction(interactionId, { done: true });
      
      // Refresh the interactions list to reflect the change
      dispatch(fetchCustomerInteractions());
      
      return { id: interactionId, done: true };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to mark interaction as done');
    }
  }
);

export const deleteCustomerInteraction = createAsyncThunk(
  'interactions/deleteCustomerInteraction',
  async (interactionId: number, { rejectWithValue, dispatch }) => {
    try {
      await directusOperations.deleteCustomerInteraction(interactionId);
      
      // Refresh the interactions list
      dispatch(fetchCustomerInteractions());
      
      return interactionId;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to delete customer interaction');
    }
  }
);

export const softDeleteCustomerInteraction = createAsyncThunk(
  'interactions/softDeleteCustomerInteraction',
  async (interactionId: number, { rejectWithValue, dispatch }) => {
    try {
      await directusOperations.softDeleteCustomerInteraction(interactionId);
      
      // Refresh the interactions list to reflect the hidden interaction
      dispatch(fetchCustomerInteractions());
      
      return interactionId;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to delete customer interaction');
    }
  }
);

export const searchCustomerInteractions = createAsyncThunk(
  'interactions/searchCustomerInteractions',
  async (params: { query: string; limit?: number; offset?: number }, { rejectWithValue }) => {
    try {
      const { query, limit = 20, offset = 0 } = params;
      const result = await directusOperations.searchCustomerInteractions(query, { limit, offset });
      
      const interactions = result.map((item: any) => normalizeInteractionData(item));
      
      return interactions;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to search customer interactions');
    }
  }
);

export const fetchTodayInteractions = createAsyncThunk(
  'interactions/fetchTodayInteractions',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState() as { interactions: InteractionsState };
      const now = Date.now();
      const lastFetched = state.interactions.lastUpdated;
      const cacheTTL = 5 * 60 * 1000; // 5 minutes cache
      
      // Check cache for dashboard optimization
      if (lastFetched && !state.interactions.isStale) {
        const lastFetchedTime = new Date(lastFetched).getTime();
        if (!isNaN(lastFetchedTime) && (now - lastFetchedTime) < cacheTTL) {
          // Return cached data by rejecting with special flag
          return rejectWithValue({ cached: true, message: 'Using cached today interactions data' });
        }
      }
      
      // Get today's date in YYYY-MM-DD format for filtering
      const today = new Date();
      const todayStr = today.toISOString().split('T')[0];
      const tomorrowStr = new Date(today.getTime() + 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      
      console.log('📅 Fetching today\'s interactions for date:', todayStr);
      
      const result = await directusOperations.getUserCustomerInteractionsWithFilter({
        limit: 50, // Get enough interactions for the day
        offset: 0,
        additionalFilter: {
          date_created: {
            _gte: todayStr + 'T00:00:00Z',
            _lt: tomorrowStr + 'T00:00:00Z'
          },
          _or: [
            { Hidden: { _null: true } },
            { Hidden: { _eq: false } }
          ] // Filter out soft deleted interactions (show null or false)
        },
        sort: ['-date_created'] // Most recent first
      });
      
      const interactions = result.map((item: any) => normalizeInteractionData(item));
      
      console.log('✅ Found', interactions.length, 'interactions for today');
      return interactions;
    } catch (error) {
      console.error('❌ Failed to fetch today\'s interactions:', error);
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch today\'s interactions');
    }
  }
);

export const fetchActivityInteractions = createAsyncThunk(
  'interactions/fetchActivityInteractions',
  async (_, { rejectWithValue }) => {
    try {
      // 🔄 NO CACHE MODE: Always fetch fresh data from Directus database
      // This ensures Activity Interactions section always shows real-time data

      // Get date range: 7 days ago to 3 days later for activity interactions
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Start of today
      const sevenDaysAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
      const threeDaysLater = new Date(today.getTime() + 3 * 24 * 60 * 60 * 1000);
      
      console.log('📅 Fetching activity interactions from', sevenDaysAgo.toDateString(), 'to', threeDaysLater.toDateString());
      
      // Get broader date range for current user only with soft delete filtering
      const result = await directusOperations.getUserCustomerInteractionsWithFilter({
        limit: 100,
        additionalFilter: {
          _or: [
            { Hidden: { _null: true } },
            { Hidden: { _eq: false } }
          ] // Filter out soft deleted interactions at API level (show null or false)
        },
        sort: ['-date_created']
      });
      
      // Apply improved filtering logic for activity interactions
      const filteredInteractions = result.filter((item: any) => {
        const createdDate = new Date(item.date_created);
        const remindDate = item.date_remind ? new Date(item.date_remind) : null;
        
        // FILTER OUT COMPLETED INTERACTIONS: Hide interactions with done=true (completed status)
        const isCompleted = normalizeDoneValue(item.done) === true;
        if (isCompleted) return false;
        
        // Show interactions that are:
        // 1. Created in the last 7 days and not completed (for recent activity)
        // 2. Scheduled (date_remind) within 3 days from today (past or future) and not completed
        // 3. Overdue reminders that are not completed (past reminders)
        
        const createdInRange = createdDate >= sevenDaysAgo;
        const hasReminder = remindDate !== null;
        
        if (hasReminder) {
          // For scheduled interactions, show if within extended range (3 days past to 3 days future)
          const threeDaysAgoFromToday = new Date(today.getTime() - 3 * 24 * 60 * 60 * 1000);
          const scheduledInRange = remindDate >= threeDaysAgoFromToday && remindDate <= threeDaysLater;
          return scheduledInRange;
        } else {
          // For non-scheduled interactions, show if created recently and still relevant
          return createdInRange;
        }
      });
      
      // Transform and sort interactions by priority and time
      const interactions = filteredInteractions.map((item: any) => {
        const createdDate = new Date(item.date_created);
        const remindDate = item.date_remind ? new Date(item.date_remind) : null;
        const currentTime = new Date();
        
        // Determine the relevant date for sorting (prefer reminder date if available)
        const relevantDate = remindDate || createdDate;
        const isOverdue = remindDate && remindDate < currentTime;
        const isToday = remindDate && remindDate.toDateString() === currentTime.toDateString();
        const timeUntilReminder = remindDate ? remindDate.getTime() - currentTime.getTime() : null;
        
        return {
          id: item.id,
          No_pelanggan: item.No_pelanggan,
          date_created: item.date_created,
          date_remind: item.date_remind,
          priority: item.priority || 'low',
          keterangan: item.keterangan,
          interaction_type: item.interaction_type,
          PIC: item.PIC,
          customer_tambak_id: item.customer_tambak_id,
          visit_id: item.visit_id,
          done: normalizeDoneValue(item.done),
          customer_name: item.customer_name || 'Unknown Customer',
          pic_name: item.pic_name || 'Unknown PIC',
          tambak_name: item.tambak_name || null,
          // Sorting metadata (serialize Date objects to strings for Redux)
          relevantDate: relevantDate.toISOString(), // ✅ Convert Date to string
          isOverdue,
          isToday,
          timeUntilReminder
        };
      });
      
      // Sort interactions by priority:
      // 1. Overdue reminders (past due but same day) - most urgent
      // 2. Today's reminders - by time
      // 3. Future reminders - by proximity
      // 4. Recent interactions without reminders - by recency
      const sortedInteractions = interactions.sort((a, b) => {
        // Priority 1: Today's overdue interactions go first
        if (a.isOverdue && a.isToday && !(b.isOverdue && b.isToday)) return -1;
        if (b.isOverdue && b.isToday && !(a.isOverdue && a.isToday)) return 1;
        
        // Priority 2: Today's interactions (overdue or upcoming) go before future
        if (a.isToday && !b.isToday) return -1;
        if (b.isToday && !a.isToday) return 1;
        
        // Priority 3: Within same day, sort by time (earliest first, overdue at top)
        if (a.isToday && b.isToday) {
          if (a.isOverdue && !b.isOverdue) return -1;
          if (b.isOverdue && !a.isOverdue) return 1;
          return new Date(a.relevantDate).getTime() - new Date(b.relevantDate).getTime();
        }
        
        // Priority 4: Future interactions by proximity (closest first)
        if (a.timeUntilReminder !== null && b.timeUntilReminder !== null) {
          return Math.abs(a.timeUntilReminder) - Math.abs(b.timeUntilReminder);
        }
        
        // Priority 5: Fallback to relevantDate (closest to now first)
        return new Date(a.relevantDate).getTime() - new Date(b.relevantDate).getTime();
      });
      
      console.log('✅ Found', sortedInteractions.length, 'activity interactions (including overdue incomplete)');
      return sortedInteractions;
    } catch (error) {
      console.error('❌ Failed to fetch activity interactions:', error);
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch activity interactions');
    }
  }
);

const interactionsSlice = createSlice({
  name: 'interactions',
  initialState,
  reducers: {
    // UI state management
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload;
    },
    
    setFilterType: (state, action: PayloadAction<string>) => {
      state.filterType = action.payload;
    },
    
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },
    
    setSelectedInteraction: (state, action: PayloadAction<CustomerInteractionRecord | null>) => {
      state.selectedInteraction = action.payload;
    },
    
    clearError: (state) => {
      state.error = null;
    },
    
    markAsStale: (state) => {
      state.isStale = true;
    },
    
    // Reset state
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Fetch interactions
      .addCase(fetchCustomerInteractions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCustomerInteractions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.interactions = action.payload.interactions;
        state.totalCount = action.payload.totalCount;
        state.currentPage = action.payload.currentPage;
        state.lastUpdated = getWIBTimeForDirectus();
        state.isStale = false;
        state.error = null;
      })
      .addCase(fetchCustomerInteractions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Fetch user interactions
      .addCase(fetchUserInteractions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserInteractions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.interactions = action.payload;
        state.lastUpdated = getWIBTimeForDirectus();
        state.isStale = false;
        state.error = null;
      })
      .addCase(fetchUserInteractions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Create interaction
      .addCase(createCustomerInteraction.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createCustomerInteraction.fulfilled, (state) => {
        state.isCreating = false;
        state.error = null;
        // The fetchCustomerInteractions will be dispatched automatically
      })
      .addCase(createCustomerInteraction.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload as string;
      })
      
      // Update interaction
      .addCase(updateCustomerInteraction.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateCustomerInteraction.fulfilled, (state) => {
        state.isUpdating = false;
        state.error = null;
      })
      .addCase(updateCustomerInteraction.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload as string;
      })
      
      // Mark interaction as done
      .addCase(markInteractionAsDone.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(markInteractionAsDone.fulfilled, (state, action) => {
        state.isUpdating = false;
        state.error = null;
        // Update the local state immediately for better UX
        const { id } = action.payload;
        const interactionIndex = state.interactions.findIndex(interaction => interaction.id === id);
        if (interactionIndex !== -1) {
          state.interactions[interactionIndex].done = true;
        }
        const todayInteractionIndex = state.todayInteractions.findIndex(interaction => interaction.id === id);
        if (todayInteractionIndex !== -1) {
          state.todayInteractions[todayInteractionIndex].done = true;
        }
      })
      .addCase(markInteractionAsDone.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload as string;
      })
      
      // Delete interaction
      .addCase(deleteCustomerInteraction.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteCustomerInteraction.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.error = null;
        // Remove from local state immediately
        state.interactions = state.interactions.filter(
          interaction => interaction.id !== action.payload
        );
      })
      .addCase(deleteCustomerInteraction.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload as string;
      })
      
      // Soft delete interaction
      .addCase(softDeleteCustomerInteraction.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(softDeleteCustomerInteraction.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.error = null;
        // Remove from local state immediately (will be filtered out when data refetches)
        state.interactions = state.interactions.filter(
          interaction => interaction.id !== action.payload
        );
      })
      .addCase(softDeleteCustomerInteraction.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload as string;
      })
      
      // Search interactions
      .addCase(searchCustomerInteractions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(searchCustomerInteractions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.interactions = action.payload;
        state.error = null;
      })
      .addCase(searchCustomerInteractions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Fetch today's interactions
      .addCase(fetchTodayInteractions.pending, (state) => {
        state.isTodayLoading = true;
        state.error = null;
      })
      .addCase(fetchTodayInteractions.fulfilled, (state, action) => {
        state.isTodayLoading = false;
        state.todayInteractions = action.payload;
        state.todayLastFetched = getWIBTimeForDirectus();
        state.error = null;
      })
      .addCase(fetchTodayInteractions.rejected, (state, action) => {
        state.isTodayLoading = false;
        // Handle cached data case
        const error = action.payload as any;
        if (error?.cached) {
          console.log('Using cached today interactions data');
        } else {
          state.error = error as string;
        }
      })
      
      // Activity Interactions (3-day period) reducers
      .addCase(fetchActivityInteractions.pending, (state) => {
        state.isTodayLoading = true;
        state.error = null;
      })
      .addCase(fetchActivityInteractions.fulfilled, (state, action) => {
        state.isTodayLoading = false;
        state.todayInteractions = action.payload; // Reuse todayInteractions for dashboard
        state.todayLastFetched = getWIBTimeForDirectus();
        state.error = null;
      })
      .addCase(fetchActivityInteractions.rejected, (state, action) => {
        state.isTodayLoading = false;
        // Handle cached data case
        const error = action.payload as any;
        if (error?.cached) {
          console.log('Using cached activity interactions data');
        } else {
          state.error = error as string;
        }
      });
  },
});

export const {
  setSearchTerm,
  setFilterType,
  setCurrentPage,
  setPageSize,
  setSelectedInteraction,
  clearError,
  markAsStale,
  resetState,
} = interactionsSlice.actions;

// Selectors
export const selectInteractionsState = (state: { interactions: InteractionsState }) => state.interactions;
export const selectInteractions = (state: { interactions: InteractionsState }) => state.interactions.interactions;
export const selectTodayInteractions = (state: { interactions: InteractionsState }) => state.interactions.todayInteractions;
export const selectTodayInteractionsLoading = (state: { interactions: InteractionsState }) => state.interactions.isTodayLoading;
export const selectTodayInteractionsCount = (state: { interactions: InteractionsState }) => state.interactions.todayInteractions.length;

// New selectors for filtering completed interactions
export const selectActiveInteractions = createSelector(
  [(state: { interactions: InteractionsState }) => state.interactions.todayInteractions],
  (interactions) => interactions.filter(interaction => interaction.done !== true)
);

export const selectActiveInteractionsCount = createSelector(
  [selectActiveInteractions],
  (activeInteractions) => activeInteractions.length
);
export const selectSelectedInteraction = (state: { interactions: InteractionsState }) => state.interactions.selectedInteraction;
export const selectInteractionsLoading = (state: { interactions: InteractionsState }) => state.interactions.isLoading;
export const selectInteractionsError = (state: { interactions: InteractionsState }) => state.interactions.error;
export const selectInteractionsCreating = (state: { interactions: InteractionsState }) => state.interactions.isCreating;
export const selectInteractionsUpdating = (state: { interactions: InteractionsState }) => state.interactions.isUpdating;
export const selectInteractionsDeleting = (state: { interactions: InteractionsState }) => state.interactions.isDeleting;
export const selectSearchTerm = (state: { interactions: InteractionsState }) => state.interactions.searchTerm;
export const selectFilterType = (state: { interactions: InteractionsState }) => state.interactions.filterType;
export const selectCurrentPage = (state: { interactions: InteractionsState }) => state.interactions.currentPage;
export const selectPageSize = (state: { interactions: InteractionsState }) => state.interactions.pageSize;
export const selectTotalCount = (state: { interactions: InteractionsState }) => state.interactions.totalCount;
export const selectIsStale = (state: { interactions: InteractionsState }) => state.interactions.isStale;
export const selectLastUpdated = (state: { interactions: InteractionsState }) => state.interactions.lastUpdated;

// Cache validation helper
export const selectIsCacheValid = (state: { interactions: InteractionsState }, maxAge: number = 5 * 60 * 1000) => {
  if (!state.interactions.lastUpdated || state.interactions.isStale) return false;
  const age = Date.now() - new Date(state.interactions.lastUpdated).getTime();
  return age < maxAge;
};

export default interactionsSlice.reducer;